import { ActionReference, GameObjectReference, GameState } from "@shared/types";
import { LitElement, TemplateResult, css, html, nothing } from "lit";
import { customElement } from "lit/decorators.js";
import { getState, performAction } from "../services/routeService";

/**
 * Custom element representing the game canvas.
 */
@customElement("game-canvas")
export class GameCanvas extends LitElement {
    public static styles = css`
        .game {
            height: 100%;
            display: grid;
            grid-template-columns: 1fr;
            grid-template-rows: auto auto 1fr auto;
            grid-column-gap: 0px;
            grid-row-gap: 0px;
        }

        .title {
            text-align: center;
            margin-top: 10px;
        }

        .header {
            display: flex;
            flex-direction: column;
            align-items: center;
            flex-grow: 1;
            position: relative;
            margin-top: 10px;
        }

        .header img {
            width: 100%;
            height: 45%;
            image-rendering: pixelated;
        }

        .header img:nth-child(n + 2) {
            position: absolute;
        }

        .content {
            flex-grow: 1;
            overflow-x: auto;
            margin-top: -200px;
            padding: 0 10px;
            z-index: 1;
        }

        .content p {
            margin: 0 0 10px 0;
        }

        .content p:last-of-type {
            margin: 0;
        }

        .footer {
            border-radius: 10px 10px 0 0;
            background-color: #52478b;
            border: 1px solid #332c57;
            margin-top: -10px;
            display: flex;
            height: 105px;
        }

        .footer .buttons {
            display: flex;
            flex-direction: column;
            overflow: auto;
            padding: 10px 10px 0 10px;
        }

        .footer .button {
            background-color: #7f6ed7;
            border: 1px solid #332c57;
            padding: 5px 10px;
            margin: 0 0 10px 10px;
            text-transform: uppercase;
            cursor: pointer;
            display: inline-block;
            user-select: none;
        }

        .footer .button.active,
        .footer .button:hover {
            background-color: #332c57;
        }

        input {
            color: white;
            background-color: black;
            text-transform: uppercase;
            border: 1px solid white;
            padding: 1em;
            border-radius: 1px;
            margin-top: 1em;
            font-family: "Onesize";
            font-size: 1em;
            letter-spacing: 0.3em;
        }
    `;

    // Properties to store game state
    private roomTitle?: string;
    private roomImages?: string[];
    private contentText?: string[];
    private requireInput?: boolean;
    private input?: string;
    private actionButtons?: ActionReference[];
    private gameObjectButtons?: GameObjectReference[];

    // Selected game actions and objects
    private selectedActionButton?: ActionReference;
    private selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    /**
     * Lifecycle method called when the component is connected to the DOM.
     */
    public connectedCallback(): void {
        super.connectedCallback();

        // Fetch and refresh the game state when connected to the DOM
        void this.refreshState();
    }

    /**
     * Refreshes the game state by fetching data from the server.
     */
    private async refreshState(): Promise<void> {
        const state: GameState = await getState();

        this.updateState(state);
    }

    /**
     * Updates the component's state based on the provided game state.
     * @param state - The game state to update the component with.
     */
    private updateState(state: GameState): void {
        // Reset the component
        this.roomTitle = state.roomTitle;
        this.roomImages = state.roomImages;
        this.contentText = state.text;
        this.requireInput = state.requireInput;
        this.input = "",
        this.actionButtons = state.actions;
        this.gameObjectButtons = state.objects;

        // Clear selected actions and objects
        this.selectedActionButton = undefined;
        this.selectedGameObjectButtons.clear();

        // Trigger a re-render of the component
        this.requestUpdate();
    }

    /**
     * Handles the click event on action buttons.
     * @param button - The action button clicked.
     */
    private async handleClickAction(button: ActionReference): Promise<void> {
        if (button.needsObject) {
            // If the action needs an object, set the selected action and clear selected objects
            this.selectedActionButton = button;
            this.selectedGameObjectButtons.clear();

            // Trigger a re-render of the component
            this.requestUpdate();
        } else {
            // If the action does not need an object, perform the action immediately
            const state: any = await performAction(button.alias, undefined, this.input);

            if (state === undefined) {
                return;
            }

            this.updateState(state);
        }
    }

    /**
     * Handles the click event on object buttons.
     * @param button - The object button clicked.
     */
    private async handleClickObject(button: GameObjectReference): Promise<void> {
        if (!this.selectedActionButton) {
            // If no action is selected, do nothing
            return;
        }

        // Add the selected object to the set
        this.selectedGameObjectButtons.add(button);

        // Perform the action with selected objects and get the updated state
        const state: GameState | undefined = await performAction(
            this.selectedActionButton.alias,
            [...this.selectedGameObjectButtons].map((e) => e.alias),
            this.input
        );

        // Clear selected action and objects if two or more objects are selected
        if (this.selectedGameObjectButtons.size >= 2) {
            this.selectedActionButton = undefined;
            this.selectedGameObjectButtons.clear();
        }

        // Trigger a re-render of the component
        this.requestUpdate();

        if (state === undefined) {
            return;
        }

        this.updateState(state);
    }

    /**
     * Renders the component template.
     * @returns The rendered template.
     */
    protected render(): TemplateResult {
        return html`
            <div class="game">
                ${this.renderTitle()} ${this.renderHeader()} ${this.renderContent()} ${this.renderFooter()}
            </div>
        `;
    }

    /**
     * Renders the title section of the component.
     * @returns The rendered title template.
     */
    private renderTitle(): TemplateResult {
        if (this.roomTitle) {
            return html`<div class="title">${this.roomTitle}</div>`;
        }

        return html`${nothing}`;
    }

    /**
     * Renders the header section of the component.
     * @returns The rendered header template.
     */
    private renderHeader(): TemplateResult {
        if (this.roomImages && this.roomImages.length > 0) {
            return html`
                <div class="header">
                    ${this.roomImages?.map((url) => html`<img src="/assets/img/rooms/${url}.png" />`)}
                </div>
            `;
        }

        return html`${nothing}`;
    }

    /**
     * Renders the content section of the component.
     * @returns The rendered content template.
     */
    private renderContent(): TemplateResult {
        return html`<div class="content">
            ${this.contentText?.map((text) => html`<p>${text}</p>`)}
            ${this.requireInput ? html`<input type="text" @change="${this.handleInputChange}">` : html``}
        </div>`;
    }

    private handleInputChange(e:Event): void {
        this.input = (e.target as HTMLInputElement).value;
    }

    /**
     * Renders the footer section of the component.
     * @returns The rendered footer template.
     */
    private renderFooter(): TemplateResult {
        return html`
            <div class="footer">
                <div class="buttons">
                    <div>
                        ${this.actionButtons?.map(
                            (button) => html`<a
                                class="button ${this.selectedActionButton === button ? "active" : ""}"
                                @click=${(): void => void this.handleClickAction(button)}
                                >${button.label}</a
                            >`
                        )}
                    </div>
                    <div>
                        ${this.selectedActionButton
                            ? this.gameObjectButtons?.map(
                                  (button) => html`<a
                                      class="button ${this.selectedGameObjectButtons.has(button)
                                          ? "active"
                                          : ""}"
                                      @click=${(): void => void this.handleClickObject(button)}
                                      >${button.name}</a
                                  >`
                              )
                            : nothing}
                    </div>
                </div>
            </div>
        `;
    }
}
